import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, Save } from "lucide-react";
import { api } from "../../lib/api";
import { ExtractionBadge } from "./PolicyBadges";

export const PolicyExtractionPanel = ({ policyId, version, canEdit, onChanged }) => {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState("");
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setData(null); setEditing(false);
    if (!version) return;
    api.get(`/policies/${policyId}/text`, { params: { version_id: version.id } })
      .then(({ data: d }) => { setData(d); setDraft(d.text); })
      .catch(() => setData({ extraction_status: "failed", extraction_error: "Not available", text: "" }));
  }, [policyId, version]);

  if (!version) return null;

  const reExtract = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post(`/policies/${policyId}/versions/${version.id}/re-extract`);
      toast[r.extraction_status === "complete" ? "success" : "warning"](`Re-extraction ${r.extraction_status}: ${r.extracted_chars} characters`);
      onChanged();
    } catch (err) { toast.error(err?.response?.data?.detail || "Re-extraction failed"); } finally { setBusy(false); }
  };
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/policies/${policyId}/versions/${version.id}/text`, { text: draft });
      toast.success("Extracted text corrected"); setEditing(false); onChanged();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); } finally { setBusy(false); }
  };

  return (
    <div className="nv-card p-5 space-y-3" data-testid="policy-extraction-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="text-h3">Extracted text <span className="text-xs font-normal text-slate-400">({version.version_label})</span></h3>
          <p className="text-[12.5px] text-slate-500">Stored for review and future use only — not connected to NestVillage AI in this phase.</p>
        </div>
        {data && <ExtractionBadge status={data.extraction_status} corrected={data.text_corrected} />}
      </div>
      {data?.extraction_error && <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2" data-testid="extraction-error">{data.extraction_error}</p>}
      {data && <p className="text-xs text-slate-500">{data.extracted_chars?.toLocaleString()} characters{data.page_count ? ` · ${data.page_count} pages` : ""}</p>}
      {editing ? (
        <textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={12} className="w-full px-3 py-2 rounded-md border border-slate-200 text-sm font-mono" data-testid="extraction-text-editor" />
      ) : (
        <pre className="whitespace-pre-wrap font-sans text-xs text-slate-700 max-h-64 overflow-y-auto bg-slate-50 rounded p-3" data-testid="extraction-text">{data ? (data.text || "(no text)") : "Loading…"}</pre>
      )}
      {canEdit && (
        <div className="flex gap-2">
          <button onClick={reExtract} disabled={busy} className="text-xs px-3 py-1.5 rounded border border-slate-200 inline-flex items-center gap-1" data-testid="extraction-retry"><RefreshCw size={13} /> Re-extract</button>
          {editing
            ? <button onClick={save} disabled={busy} className="text-xs px-3 py-1.5 rounded bg-sky-700 text-white inline-flex items-center gap-1" data-testid="extraction-save"><Save size={13} /> Save corrected text</button>
            : <button onClick={() => setEditing(true)} className="text-xs px-3 py-1.5 rounded border border-slate-200" data-testid="extraction-edit">Correct text manually</button>}
        </div>
      )}
    </div>
  );
};
